import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { BellIcon, TrashIcon, CalendarDaysIcon, CheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { BellIcon as BellSolid } from '@heroicons/react/24/solid';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';

import { useNavigate } from 'react-router-dom';

// Function to generate the query key dynamically based on user context
export const getNotifQueryKey = (userId, role) => ['notifications', userId, role];

const NotificationCenter = ({ placement = 'top-right' }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const queryKey = getNotifQueryKey(user?._id, user?.role);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  /* ── Fetch notifications (uses axios default credentials — no manual token) ── */
  const { data: notifications = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await axios.get(`${apiUrl}/notifications`, { params: { role: user?.role } });
      return Array.isArray(res.data) ? res.data : [];
    },
    refetchInterval: 60000,
    staleTime: 10000,
    retry: 1,
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  /* ── Click-outside close ── */
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── ESC to close ── */
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  /* ── Mutations — all invalidate the shared key ── */
  const markRead = useMutation({
    mutationFn: (id) => axios.put(`${apiUrl}/notifications/${id}/read`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  const markAllRead = useMutation({
    mutationFn: () => axios.put(`${apiUrl}/notifications/read-all`, { role: user?.role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  const deleteNotif = useMutation({
    mutationFn: (id) => axios.delete(`${apiUrl}/notifications/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  const handleToggle = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) queryClient.invalidateQueries({ queryKey });
      return !prev;
    });
  }, [queryClient, queryKey]);

  const handleClick = (notif) => {
    if (!notif.read) markRead.mutate(notif._id);
    setIsOpen(false);
    
    // Navigation
    if (notif.relatedModel === 'Appointment') {
      if (user?.role === 'pro') {
        navigate('/pro/appointments'); // Could pass ?id=notif.relatedId if supported
      } else {
        navigate('/appointments');
      }
    }
  };

  /* ── Dropdown position ── */
  const dropdownPos = placement === 'bottom-left'
    ? 'bottom-full left-0 mb-3'
    : 'top-full right-0 mt-2';

  return (
    <div className="relative flex items-center" ref={dropdownRef}>

      {/* Bell */}
      <button
        onClick={handleToggle}
        className={`relative p-2 rounded-xl transition-all duration-200 focus:outline-none ${
          isOpen ? 'bg-primary-50 text-reglow-primary' : 'hover:bg-gray-100 text-gray-500'
        }`}
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        {unreadCount > 0
          ? <BellSolid className="h-5 w-5 text-reglow-primary" />
          : <BellIcon className="h-5 w-5" />
        }
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-4 min-w-[16px] px-0.5 text-[9px] font-bold text-white bg-red-500 rounded-full border-2 border-white leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={`absolute ${dropdownPos} z-[200] bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col`}
          style={{
            width: 'min(340px, 92vw)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
            animation: 'notifSlideIn 0.18s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
          role="dialog"
          aria-label="Centre de notifications"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50 shrink-0">
            <div className="flex items-center gap-2">
              <h3 className="text-[13px] font-bold text-gray-900 tracking-tight">Notifications</h3>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 text-[9px] font-bold text-white bg-reglow-primary rounded-full leading-none">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="flex items-center gap-1 text-[11px] font-bold text-gray-400 hover:text-reglow-primary transition-colors disabled:opacity-50"
              >
                <CheckIcon className="h-3 w-3" />
                Tout lire
              </button>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1" style={{ maxHeight: '340px' }}>
            {isLoading ? (
              <div className="py-8 flex items-center justify-center">
                <div className="h-5 w-5 border-2 border-primary-100 border-t-reglow-primary rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-10 flex flex-col items-center text-center px-6">
                <div className="h-10 w-10 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center mb-3">
                  <BellIcon className="h-5 w-5 text-gray-300" />
                </div>
                <p className="text-[13px] font-bold text-gray-500">Aucune notification</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Vous êtes à jour !</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {notifications.map((notif) => (
                  <div
                    key={notif._id}
                    className={`relative flex items-start gap-3 px-4 py-3 cursor-pointer group transition-colors ${
                      !notif.read ? 'bg-primary-50/30 hover:bg-primary-50/60' : 'hover:bg-gray-50/80'
                    }`}
                    onClick={() => handleClick(notif)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleClick(notif)}
                  >
                    {/* Unread indicator */}
                    {!notif.read && (
                      <div className="absolute left-0 top-3 bottom-3 w-[3px] bg-reglow-primary rounded-r-full" />
                    )}

                    {/* Icon */}
                    <div className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center mt-0.5 transition-colors ${
                      !notif.read ? 'bg-primary-100 text-reglow-primary' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <CalendarDaysIcon className="h-4 w-4" />
                    </div>

                    {/* Text */}
                    <div className="flex-1 min-w-0 pr-7">
                      <div className="flex items-baseline gap-2 justify-between">
                        <p className={`text-[12px] font-bold leading-tight truncate ${
                          !notif.read ? 'text-gray-900' : 'text-gray-600'
                        }`}>
                          {notif.title}
                        </p>
                        <span className="text-[10px] text-gray-400 whitespace-nowrap shrink-0 font-medium">
                          {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true, locale: fr })}
                        </span>
                      </div>
                      {notif.message && (
                        <p className="text-[11px] text-gray-500 leading-snug mt-0.5 line-clamp-2">
                          {notif.message}
                        </p>
                      )}
                    </div>

                    {/* Delete */}
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteNotif.mutate(notif._id); }}
                      className="absolute right-2 top-2.5 opacity-0 group-hover:opacity-100 p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-150"
                      title="Supprimer"
                      aria-label="Supprimer la notification"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-gray-50 bg-gray-50/50 shrink-0">
            <Link
              to={user?.role === 'pro' ? '/pro/notifications' : '/notifications'}
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-center gap-1.5 text-[11px] font-bold text-reglow-primary hover:text-primary-700 transition-colors"
            >
              Voir toutes les notifications
              <ArrowRightIcon className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}

      <style>{`
        @keyframes notifSlideIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default NotificationCenter;

